import React, { useState } from "react";
import axios from "axios";
import { Lock, ArrowRight, Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoAccess() {
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function enter(e) {
    e?.preventDefault?.();
    setError(null);
    if (!passcode.trim()) {
      setError("Enter the demo passcode.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/demo/access`, { passcode: passcode.trim() });
      // Reuse the real app's session mechanism (localStorage 'user').
      const userToStore = { ...data };
      delete userToStore.show_feature_announcement;
      delete userToStore.current_feature_version;
      localStorage.setItem("user", JSON.stringify(userToStore));
      // Hard navigation so App re-initializes from stored session.
      window.location.href = "/dashboard";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to open the demo. Please try again.");
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center">
            <span className="text-white font-extrabold text-xl">D</span>
          </div>
          <div className="text-left">
            <div className="font-extrabold text-slate-900 text-lg leading-tight">DrawReady</div>
            <div className="text-xs text-slate-400 font-medium">Construction Finance</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded mb-3">
            <Building2 className="w-3.5 h-3.5" /> Lender Demo Access
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900">Enter the live demo</h1>
          <p className="text-sm text-slate-500 mt-1">
            Explore the full DrawReady platform with a realistic sample portfolio. Enter the passcode you were given.
          </p>

          <form onSubmit={enter} className="mt-5">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Demo passcode</label>
            <div className="mt-1.5 flex items-center gap-2 border border-slate-300 rounded-lg px-3 h-12 bg-slate-50 focus-within:border-blue-500">
              <Lock className="w-4 h-4 text-slate-400" />
              <input
                data-testid="demo-passcode-input"
                type="text"
                autoComplete="off"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="e.g. DrawReady2026"
                className="flex-1 bg-transparent outline-none text-slate-900"
              />
            </div>

            {error ? <p data-testid="demo-error" className="text-red-600 text-sm mt-3">{error}</p> : null}

            <button
              data-testid="demo-enter-button"
              type="submit"
              disabled={loading}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12 rounded-lg transition-colors disabled:opacity-60"
            >
              {loading ? "Opening demo…" : <>Enter demo <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-[11px] text-slate-400 mt-4 text-center">
            Sample data only. This demo environment is isolated from live accounts.
          </p>
        </div>
      </div>
    </div>
  );
}
